import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

/**
 * ReadyStep Component
 *
 * Final step of onboarding wizard - completion summary and CTAs
 * Design: One prominent primary action, secondary actions as text links
 */

interface CompletedItem {
  icon?: string;
  label: string;
}

interface ReadyStepProps {
  completedItems: CompletedItem[];
  onCreatePost: () => void;
  onExploreCalendar: () => void;
  onGoToDashboard: () => void;
  primaryActionLabel?: string;
  primaryActionIcon?: string;
}

export function ReadyStep({
  completedItems,
  onCreatePost,
  onExploreCalendar,
  onGoToDashboard,
  primaryActionLabel,
  primaryActionIcon,
}: ReadyStepProps) {
  const { t } = useTranslation();

  return (
    <div className="text-center">
      {/* Success Icon with celebration */}
      <div className="mb-6 flex justify-center">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center animate-fade-in">
          <span className="text-4xl">🎉</span>
        </div>
      </div>

      {/* Heading */}
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        {t('onboarding_ready_title')}
      </h2>
      <p className="text-gray-600 mb-6">
        {t('onboarding_ready_description')}
      </p>

      {/* Completed Items - Compact chips */}
      <div className="mb-8 flex flex-wrap justify-center gap-2">
        {completedItems.map((item, index) => (
          <div
            key={index}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5',
              'bg-green-50 border border-green-200 rounded-full',
              'animate-fade-in'
            )}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <Icon name={ICONS.CHECK} size="xs" color="text-green-600" ariaHidden />
            <span className="text-sm text-green-800">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Primary CTA */}
      <Button
        variant="primary"
        size="lg"
        onClick={onCreatePost}
        className="w-full mb-4"
      >
        <Icon name={primaryActionIcon ?? ICONS.MAGIC_WAND} size="md" color="text-white" className="mr-2" ariaHidden />
        {primaryActionLabel ?? t('onboarding_ready_create_post')}
      </Button>

      {/* Secondary options as simple text links */}
      <div className="flex items-center justify-center gap-4 text-sm">
        <button
          type="button"
          onClick={onExploreCalendar}
          className="text-gray-600 hover:text-primary-600 transition-colors"
        >
          {t('onboarding_ready_explore_calendar')}
        </button>
        <span className="text-gray-300">|</span>
        <button
          type="button"
          onClick={onGoToDashboard}
          className="text-gray-600 hover:text-primary-600 transition-colors"
        >
          {t('onboarding_ready_go_dashboard')}
        </button>
      </div>
    </div>
  );
}
